import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ModalTitle } from 'react-bootstrap';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { useImpressao } from '../../ImpressaoContext';
import { FaRegEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';

interface Props {
  selectedOption: any;
  modalTitle: string;
  setModalTitle: (value: string) => void;
}

const EditDescriptionContainer = styled.div<{
  optionSelected: boolean;
  isEditing: boolean;
}>`
  min-width: 579.09px;
  padding: 13px ${({ isEditing }) => (isEditing ? '8px' : '2px')};
  border-radius: 3.5px;
  transition: background-color 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;

  background-color: ${({ isEditing }) =>
    isEditing ? 'rgba(235, 235, 235, 0.75)' : 'transparent'};

  &:hover {
    background-color: ${({ optionSelected }) =>
      optionSelected ? 'rgba(235, 235, 235, 0.75)' : 'transparent'};
    cursor: ${({ optionSelected }) => (optionSelected ? 'pointer' : 'default')};
  }

  height: 2.5rem; /* altura fixa pra evitar aumento */
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

const TitleInput = styled.input`
  font-size: 1.28571429rem;
  font-weight: 500;
  line-height: 1.42857143;
  color: #172b4d;
  background-color: transparent;
  border: none;
  width: 100%;
  padding-right: 60px; /* espaço pros ícones */
  outline: none;
`;

const ButtonGroup = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  gap: 4px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 1.2rem;

  &:hover {
    opacity: 0.7;
  }
`;

export const EditableModalTitle: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    selectedOption,
    handleSelectedOption,
    handleOptionsSelect,
    optionsSelect,
    handleIsChangeView,
    handleSelectedView,
    isChangeView,
    views,
  } = useImpressao();

  const handleContainerClick = () => {
    if (selectedOption) {
      setIsEditing(true);
      setTempValue(selectedOption?.label);
    }
  };

  const handleConfirm = useCallback(() => {
    if (selectedOption) {
      setTimeout(() => {
        const finalValue = tempValue.trimStart().trimEnd().toUpperCase();

        if (!finalValue || finalValue.length === 0 || finalValue === '') {
          toast.warning('A descrição deve ser informada');
          return;
        }

        const newSelected = {
          label: finalValue,
          value: selectedOption.value,
        };

        handleSelectedOption(newSelected);

        const updatedOptions = optionsSelect.filter(
          (opt) => opt.value !== selectedOption.value,
        );

        handleOptionsSelect([...updatedOptions, newSelected]);

        const selectedsFromView = views.find(
          (view) => view.cod_impressao === selectedOption.value,
        );

        if (selectedsFromView) {
          const selected = {
            ...selectedsFromView,
            des_impressao: finalValue,
          };
          handleSelectedView(selected);
        }

        handleIsChangeView(true);

        setIsEditing(false);
      }, 120);
    }
  }, [
    handleIsChangeView,
    handleOptionsSelect,
    handleSelectedOption,
    handleSelectedView,
    optionsSelect,
    selectedOption,
    tempValue,
    views,
  ]);

  const handleCancel = () => {
    if (selectedOption) {
      setTimeout(() => {
        setTempValue(selectedOption?.label);
        setIsEditing(false);
      }, 120);
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        handleConfirm();
      }
    },
    [handleConfirm],
  );

  useEffect(() => {
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) handleIsChangeView(false);
    else handleIsChangeView(true);

    if (e.target.value.length <= 50) {
      setTempValue(e.target.value.toUpperCase());
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') handleConfirm();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleConfirm]);

  return (
    <EditDescriptionContainer
      optionSelected={!!selectedOption}
      onClick={handleContainerClick}
      isEditing={isEditing}
    >
      {isEditing && (
        <InputWrapper ref={wrapperRef}>
          <TitleInput
            value={tempValue.toUpperCase()}
            onChange={handleChange}
            maxLength={50}
            autoFocus
            ref={inputRef}
          />
          <ButtonGroup>
            <IconButton
              type="button"
              onClick={handleConfirm}
              disabled={!isChangeView}
            >
              <AiOutlineCheck color="#28a745" />
            </IconButton>
            <IconButton type="button" onClick={handleCancel}>
              <AiOutlineClose color="#dc3545" />
            </IconButton>
          </ButtonGroup>
        </InputWrapper>
      )}
      {!isEditing && (
        <ModalTitle>
          {selectedOption?.label?.toUpperCase()}
          <FaRegEdit
            color="#949494df"
            size={18}
            style={{ marginTop: '-0.3125rem', marginLeft: '0.3125rem' }}
          />{' '}
        </ModalTitle>
      )}
    </EditDescriptionContainer>
  );
};
